import { Suspense } from 'react'
import { createRoute } from '@tanstack/react-router'
import { useSuspenseQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { authLayoutRoute } from './_auth'
import {
	PlaylistForm,
	type PlaylistFormValues,
	PlaylistTracklist,
	PlaylistDetailSkeleton,
	PlaylistHero,
	PlaylistDangerZone,
	PlaylistSpotifySync,
} from '@/components/playlist'
import { api, queryClient } from '@/lib/api'
import { playlistQueryOptions } from '@/lib/queries/playlists'
import { songsQueryOptions } from '@/lib/queries/songs'
import { usePlayback } from '@/lib/playback-context'
import { getPlaylistColor } from '@/lib/playlist-colors'

export const playlistDetailRoute = createRoute({
	getParentRoute: () => authLayoutRoute,
	path: '/playlists/$id',
	loader: ({ params }) => {
		queryClient.ensureQueryData(playlistQueryOptions(params.id))
		queryClient.ensureQueryData(songsQueryOptions(params.id))
	},
	component: PlaylistDetailPage,
})

function PlaylistDetailPage() {
	return (
		<main className="flex-1">
			<Suspense fallback={<PlaylistDetailSkeleton />}>
				<PlaylistDetailContent />
			</Suspense>
		</main>
	)
}

function PlaylistDetailContent() {
	const { id } = playlistDetailRoute.useParams()
	const { data: playlist } = useSuspenseQuery(playlistQueryOptions(id))
	const { data: songs } = useSuspenseQuery(songsQueryOptions(id))
	const { state: playbackState } = usePlayback()
	const queryClientHook = useQueryClient()

	const isPlayingThisPlaylist = playbackState.status === 'playing' && playbackState.playlistId === id

	const updateMutation = useMutation({
		mutationFn: async (values: PlaylistFormValues) => {
			const response = await api.api.playlists({ id }).patch({
				name: values.name,
				icon: values.icon || undefined,
				color: values.color || undefined,
				spotifyPlaylistId: values.spotifyPlaylistId || null,
			})
			if (response.error) {
				throw new Error('Failed to update playlist')
			}
			return response.data
		},
		onSuccess: () => {
			queryClientHook.invalidateQueries({ queryKey: ['playlists'] })
		},
	})

	const handleUpdate = async (values: PlaylistFormValues) => {
		await updateMutation.mutateAsync(values)
	}

	const accentColor = getPlaylistColor(playlist.color)

	return (
		<div className="relative">
			{/* Atmospheric gradient backdrop */}
			<div
				className="pointer-events-none absolute inset-x-0 top-0 h-72"
				style={{
					background: `radial-gradient(ellipse 80% 50% at 50% 0%, color-mix(in oklch, ${accentColor} 10%, transparent) 0%, transparent 100%)`,
				}}
			/>

			<div className="relative mx-auto max-w-2xl px-6">
				<PlaylistHero
					name={playlist.name}
					icon={playlist.icon}
					accentColor={accentColor}
					songs={songs}
					playlistId={id}
				/>
				<PlaylistTracklist
					songs={songs}
					playlistId={id}
					accentColor={accentColor}
					currentIndex={playlist.currentIndex}
					currentSongId={isPlayingThisPlaylist ? playbackState?.songId : undefined}
					isPlaying={isPlayingThisPlaylist}
				/>

				<section className="mt-10">
					<h3 className="mb-4 text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">
						Details
					</h3>
					<PlaylistForm
						key={id}
						defaultValues={{
							name: playlist.name,
							icon: playlist.icon ?? '',
							color: playlist.color ?? '',
							spotifyPlaylistId: playlist.spotifyPlaylistId ?? '',
						}}
						onSubmit={handleUpdate}
						autoSave
					/>
				</section>

				{playlist.spotifyPlaylistId && (
					<PlaylistSpotifySync playlistId={id} />
				)}

				<PlaylistDangerZone
					playlistId={id}
					playlistName={playlist.name}
				/>
			</div>
		</div>
	)
}
